// js/components/Form.js
import Delegate from 'ftdomdelegate';

class CosForm extends HTMLElement {
  constructor() {
    super();
    this.domDelegate = new Delegate(this);
    this.form = this.querySelector('form');
  }

  connectedCallback() {
    this._initializeSettings();
    this._attachListeners();
    this._initializeForm();
  }

  _initializeSettings() {
    // Parse settings from data-settings attribute
    const settingsData = this.getAttribute('data-settings');
    if (settingsData) {
      try {
        this.settings = JSON.parse(settingsData);
      } catch (e) {
        console.warn('Invalid data-settings JSON:', e);
        this.settings = {};
      }
    } else {
      this.settings = {};
    }
  }

  disconnectedCallback() {
    this.domDelegate.off();
  }

  _attachListeners() {
    // Listen for form submission
    this.domDelegate.on('submit', 'form', this._handleSubmit.bind(this));
    
    // Listen for input validation on blur
    this.domDelegate.on('blur', 'input, textarea, select', this._handleInputBlur.bind(this));
    
    // Listen for input validation on change
    this.domDelegate.on('change', 'input, textarea, select', this._handleInputChange.bind(this));
    
    // Listen for input validation on input (real-time)
    this.domDelegate.on('input', 'input, textarea, select', this._handleInputInput.bind(this));
  }

  _initializeForm() {
    // Set required fields based on .required class
    this._setRequiredFields();
    
    // Add error icons to all input wrappers
    this._addErrorIcons();
  }

  _setRequiredFields() {
    // Find all fields with [required] attribute and ensure they are properly set
    const requiredFields = this.querySelectorAll('[required]');
    requiredFields.forEach(field => {
      if (field.tagName === 'INPUT' || field.tagName === 'SELECT' || field.tagName === 'TEXTAREA') {
        field.required = true;
      }
    });
  }

  _addErrorIcons() {
    const inputWrappers = this.querySelectorAll('.form__input-wrapper');
    
    inputWrappers.forEach(wrapper => {
      const input = wrapper.querySelector('input, textarea, select');
      if (input && !wrapper.querySelector('.input-error-icon')) {
        const errorIcon = document.createElement('div');
        errorIcon.className = 'input-error-icon';
        errorIcon.innerHTML = window.theme.icons.infoCircle;

        // wrapp the input and the icon in a div
        const inputWrapper = document.createElement('div');
        inputWrapper.className = 'relative';
        inputWrapper.appendChild(input);
        inputWrapper.appendChild(errorIcon);
        wrapper.appendChild(inputWrapper);
        
        // Position the icon relative to the input
        wrapper.style.position = 'relative';
        // wrapper.appendChild(errorIcon);
      }
    });
  }

  _handleSubmit(event) {
    const form = event.target;
    
    // Use native HTML5 validation
    if (!form.checkValidity()) {
      event.preventDefault();
      
      // Find first invalid field and show error
      const invalidFields = form.querySelectorAll(':invalid');
      if (invalidFields.length > 0) {
        const firstInvalidField = invalidFields[0];
        this._showInputError(firstInvalidField);
        firstInvalidField.focus();
      }
      
      return false;
    }
    
    // Clear all errors if form is valid
    this._clearAllErrors();
    return true;
  }

  _handleInputBlur(event) {
    const input = event.target;
    this._validateInput(input);
  }

  _handleInputChange(event) {
    const input = event.target;
    this._validateInput(input);
  }

  _handleInputInput(event) {
    const input = event.target;
    
    // For real-time validation, only clear errors if input becomes valid
    if (input.checkValidity()) {
      this._clearInputError(input);
    }
  }

  _validateAllInputs() {
    const inputs = this.querySelectorAll('input, textarea, select');
    
    inputs.forEach(input => {
      this._validateInput(input);
    });
  }

  _validateInput(input) {
    const isValid = input.checkValidity();
    const wrapper = input.closest('.form__input-wrapper');
    
    if (!isValid) {
      this._showInputError(input, wrapper);
    } else {
      this._clearInputError(input, wrapper);
    }
    
    return isValid;
  }

  _showInputError(input, wrapper) {
    if (!wrapper) wrapper = input.closest('.form__input-wrapper');
    if (!wrapper) return;
    
    wrapper.classList.add('has-error');
    input.classList.add('form__input--error');
    
    // Show error icon
    const errorIcon = wrapper.querySelector('.input-error-icon');
    if (errorIcon) {
      errorIcon.style.display = 'flex';
    }
    
    // Show error message with translation
    this._showFieldErrorMessage(input, wrapper);
  }

  _showFieldErrorMessage(input, wrapper) {
    // Get translation key based on input type and validation state
    let messageKey = 'required';
    
    if (input.type === 'email' && input.validity.typeMismatch) {
      messageKey = 'email_invalid';
    } else if (input.type === 'password' && input.validity.tooShort) {
      messageKey = 'password_too_short';
    } else if (input.type === 'tel' && input.validity.patternMismatch) {
      messageKey = 'phone_invalid';
    } else if (input.name && input.name.includes('name') && input.validity.valueMissing) {
      messageKey = 'name_required';
    }
    
    // Get translated message
    const message = this._getTranslatedMessage(messageKey);
    
    // Add error message
    let errorMessage = wrapper.querySelector('.form__error-message');
    if (!errorMessage) {
      errorMessage = document.createElement('div');
      errorMessage.className = 'form__error-message f12 fw500 text-error mt-xs';
      wrapper.appendChild(errorMessage);
    }
    errorMessage.textContent = message;
  }

  _getTranslatedMessage(key) {
    // Try to get translation from component settings first
    if (this.settings?.translations?.[key]) {
      return this.settings.translations[key];
    }
    
    // Try to get translation from window.theme
    if (window.theme?.translations?.form?.validation?.[key]) {
      return window.theme.translations.form.validation[key];
    }
    
    // Fallback messages
    const fallbacks = {
      'required': 'Acest câmp este obligatoriu',
      'email_invalid': 'Te rugăm să introduci o adresă de email validă',
      'password_too_short': 'Parola trebuie să aibă cel puțin 8 caractere',
      'phone_invalid': 'Te rugăm să introduci un număr de telefon valid',
      'name_required': 'Te rugăm să introduci numele tău complet'
    };
    
    return fallbacks[key] || 'Acest câmp este obligatoriu';
  }

  _clearInputError(input, wrapper) {
    if (!wrapper) wrapper = input.closest('.form__input-wrapper');
    if (!wrapper) return;
    
    wrapper.classList.remove('has-error');
    input.classList.remove('form__input--error');
    
    // Hide error icon
    const errorIcon = wrapper.querySelector('.input-error-icon');
    if (errorIcon) {
      errorIcon.style.display = 'none';
    }
  }

  _clearAllErrors() {
    // Remove error classes from all wrappers
    const errorWrappers = this.querySelectorAll('.has-error');
    errorWrappers.forEach(wrapper => wrapper.classList.remove('has-error'));
    
    // Remove error classes from all inputs
    const errorInputs = this.querySelectorAll('.form__input--error');
    errorInputs.forEach(input => input.classList.remove('form__input--error'));
    
    // Hide all error icons
    const errorIcons = this.querySelectorAll('.input-error-icon');
    errorIcons.forEach(icon => icon.style.display = 'none');
    
    // Remove error messages
    const errorMessages = this.querySelectorAll('.form__error-message');
    errorMessages.forEach(message => message.remove());
  }

  _hasValidationErrors() {
    const errorWrappers = this.querySelectorAll('.form__input-wrapper.has-error');
    return errorWrappers.length > 0;
  }

  _focusFirstError() {
    const firstErrorInput = this.querySelector('.form__input-wrapper.has-error input, .form__input-wrapper.has-error textarea, .form__input-wrapper.has-error select');
    if (firstErrorInput) {
      firstErrorInput.focus();
    }
  }

  // Public method to clear all errors
  clearAllErrors() {
    const errorWrappers = this.querySelectorAll('.form__input-wrapper.has-error');
    errorWrappers.forEach(wrapper => {
      const input = wrapper.querySelector('input, textarea, select');
      if (input) {
        this._clearInputError(input, wrapper);
      }
    });
  }

  // Public method to show custom error for specific field
  showFieldError(fieldName, message) {
    const input = this.querySelector(`[name="${fieldName}"]`);
    if (input) {
      const wrapper = input.closest('.form__input-wrapper');
      if (wrapper) {
        this._showInputError(input, wrapper);
        
        // Add custom error message if needed
        if (message) {
          let errorMessage = wrapper.querySelector('.form__error-message');
          if (!errorMessage) {
            errorMessage = document.createElement('div');
            errorMessage.className = 'form__error-message f12 fw500 text-error mt-xs';
            wrapper.appendChild(errorMessage);
          }
          errorMessage.textContent = message;
        }
      }
    }
  }

  // Public method to clear custom error for specific field
  clearFieldError(fieldName) {
    const input = this.querySelector(`[name="${fieldName}"]`);
    if (input) {
      const wrapper = input.closest('.form__input-wrapper');
      if (wrapper) {
        this._clearInputError(input, wrapper);
        
        // Remove custom error message
        const errorMessage = wrapper.querySelector('.form__error-message');
        if (errorMessage) {
          errorMessage.remove();
        }
      }
    }
  }
}

if (!window.customElements.get('cos-form')) {
  window.customElements.define('cos-form', CosForm);
}
